import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    Container, Typography, Button, Paper, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Box,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Snackbar, Alert, alpha, useTheme, Chip, Checkbox, Tooltip, CircularProgress
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon, Visibility as ViewIcon, ArrowBack, ContentCopy as DuplicateIcon, CompareArrows as CompareIcon, Terminal as TerminalIcon, Share as ShareIcon } from "@mui/icons-material";
import { useAuth, useThemeMode, vortxColors, vortxTypography, useRetroContextSafe, API_BASE_URL, retroColors } from '@vortx/shared-ui';
import ScenarioCompareModal from '../components/ScenarioCompareModal';
import ScenarioCompareModalV2 from '../components/ScenarioCompareModalV2';
import { SharedPagesDashboard, ShareConfigDialog } from '@vortx/shared-ui';

export default function ProjectAnalysis() {
    const { projectId } = useParams();
    const [project, setProject] = useState(null);
    const [scenarios, setScenarios] = useState([]);
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser } = useAuth();
    const { setProjectContext } = useRetroContextSafe();

    // Dialog States
    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
    const [selectedScenarioId, setSelectedScenarioId] = useState(null);
    const [selectedScenarioName, setSelectedScenarioName] = useState("");
    const [tempName, setTempName] = useState("");
    const [duplicateName, setDuplicateName] = useState("");
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    // Project rename dialog state
    const [projectRenameDialogOpen, setProjectRenameDialogOpen] = useState(false);
    const [tempProjectName, setTempProjectName] = useState("");

    // Scenario comparison selection state
    const [selectedScenarioIds, setSelectedScenarioIds] = useState(new Set());
    const [compareModalOpen, setCompareModalOpen] = useState(false);
    const [compareScenarios, setCompareScenarios] = useState([]);
    const [loadingCompare, setLoadingCompare] = useState(false);
    const [sharedPagesOpen, setSharedPagesOpen] = useState(false);

    // Single-scenario share state
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareScenarioId, setShareScenarioId] = useState(null);
    const [shareScenarioName, setShareScenarioName] = useState('');
    const [shareScenarioData, setShareScenarioData] = useState(null);

    // Comparison view mode (classic table vs dashboard visuals)
    const [compareViewMode, setCompareViewMode] = useState(() => {
        return localStorage.getItem('vortx-compare-view') || 'dashboard';
    });

    const handleToggleCompareView = () => {
        const newMode = compareViewMode === 'classic' ? 'dashboard' : 'classic';
        setCompareViewMode(newMode);
        localStorage.setItem('vortx-compare-view', newMode);
    };

    const theme = useTheme();
    const { isRetro } = useThemeMode();

    // Theme-aware colors - retro mode uses transparent backgrounds
    const bgMain = isRetro ? 'transparent' : theme.palette.background.default;
    const bgCard = isRetro ? 'rgba(0, 0, 0, 0.4)' : theme.palette.background.paper;
    const textPrimary = isRetro ? '#f4f1ec' : theme.palette.text.primary;
    const textSecondary = isRetro ? '#888' : theme.palette.text.secondary;

    const getHeaders = async () => {
        const headers = { 'Content-Type': 'application/json' };
        if (currentUser) {
            const token = await currentUser.getIdToken();
            headers['Authorization'] = `Bearer ${token}`;
        } else {
            headers['X-Dev-User-Id'] = 'tim@vortx.us';
        }
        return headers;
    };

    // Reload scenarios when navigating to this page (location.key changes on navigation)
    useEffect(() => {
        loadProjectAndScenarios();
    }, [projectId, currentUser, location.key]);

    const loadProjectAndScenarios = async () => {
        try {
            const headers = await getHeaders();

            // Fetch project details with scenarios
            const projectRes = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${projectId}`, { headers });
            if (projectRes.ok) {
                const projectData = await projectRes.json();
                setProject(projectData);
                setProjectContext(projectData, null); // Update retro breadcrumb
            }

            // Fetch all scenarios for this project
            const scenariosRes = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios`, { headers });
            if (scenariosRes.ok) {
                const scenariosData = await scenariosRes.json();
                setScenarios(scenariosData.scenarios || []);
            }
        } catch (error) {
            console.error("Failed to load project data", error);
        }
    };

    const handleRename = async () => {
        if (!selectedScenarioId) return;
        try {
            const headers = await getHeaders();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios/${selectedScenarioId}`, {
                method: "PUT",
                headers,
                body: JSON.stringify({ name: tempName })
            });
            if (res.ok) {
                setRenameDialogOpen(false);
                setSnackbar({ open: true, message: "Scenario renamed", severity: "success" });
                loadProjectAndScenarios(); // Refresh list
            } else {
                const errorText = await res.text();
                console.error("Rename failed:", res.status, errorText);
                throw new Error(`Rename failed: ${res.status} - ${errorText}`);
            }
        } catch (e) {
            console.error("Rename error:", e);
            setSnackbar({ open: true, message: `Rename failed: ${e.message}`, severity: "error" });
        }
    };

    const handleDelete = async () => {
        if (!selectedScenarioId) return;
        try {
            const headers = await getHeaders();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios/${selectedScenarioId}`, {
                method: "DELETE",
                headers
            });
            if (res.ok) {
                setDeleteDialogOpen(false);
                setSnackbar({ open: true, message: "Scenario deleted", severity: "success" });
                loadProjectAndScenarios(); // Refresh list
            } else {
                throw new Error("Delete failed");
            }
        } catch (e) {
            setSnackbar({ open: true, message: "Delete failed", severity: "error" });
        }
    };

    const openRename = (id, currentName) => {
        setSelectedScenarioId(id);
        setTempName(currentName);
        setRenameDialogOpen(true);
    };

    const openDelete = (id, name) => {
        setSelectedScenarioId(id);
        setSelectedScenarioName(name);
        setDeleteDialogOpen(true);
    };

    const openProjectRename = () => {
        setTempProjectName(project?.name || "");
        setProjectRenameDialogOpen(true);
    };

    const handleProjectRename = async () => {
        if (!project) return;
        try {
            const headers = await getHeaders();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${projectId}`, {
                method: "PUT",
                headers,
                body: JSON.stringify({ name: tempProjectName })
            });
            if (res.ok) {
                setProjectRenameDialogOpen(false);
                setSnackbar({ open: true, message: "Project renamed", severity: "success" });
                loadProjectAndScenarios(); // Refresh to get updated project name
            } else {
                const errorText = await res.text();
                console.error("Project rename failed:", res.status, errorText);
                throw new Error(`Rename failed: ${res.status} - ${errorText}`);
            }
        } catch (e) {
            console.error("Project rename error:", e);
            setSnackbar({ open: true, message: `Rename failed: ${e.message}`, severity: "error" });
        }
    };

    const openDuplicate = (id, name) => {
        setSelectedScenarioId(id);
        setSelectedScenarioName(name);
        setDuplicateName(`${name} (Copy)`);
        setDuplicateDialogOpen(true);
    };

    const openShareDialog = async (scenarioId, scenarioName) => {
        setShareScenarioId(scenarioId);
        setShareScenarioName(scenarioName);
        setShareDialogOpen(true);

        try {
            const headers = await getHeaders();
            const res = await fetch(
                `${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios/${scenarioId}`,
                { headers, cache: 'no-store' }
            );
            if (res.ok) {
                const fullScenario = await res.json();
                setShareScenarioData(fullScenario);
            }
        } catch (error) {
            console.error('Error fetching scenario for share:', error);
        }
    };

    // Comparison selection handlers
    const toggleScenarioSelection = (scenarioId) => {
        setSelectedScenarioIds(prev => {
            const next = new Set(prev);
            if (next.has(scenarioId)) {
                next.delete(scenarioId);
            } else {
                if (next.size >= 5) {
                    setSnackbar({ open: true, message: 'Maximum 5 scenarios for comparison', severity: 'warning' });
                    return prev;
                }
                next.add(scenarioId);
            }
            return next;
        });
    };

    const toggleSelectAll = () => {
        const selectableScenarios = scenarios.filter(s => ['completed', 'optimal', 'Optimal Obtained'].includes(s.status) || s.reopt_results || s.last_run_uuid);
        if (selectedScenarioIds.size === selectableScenarios.length) {
            setSelectedScenarioIds(new Set());
        } else {
            const idsToSelect = selectableScenarios.slice(0, 5).map(s => s.id);
            setSelectedScenarioIds(new Set(idsToSelect));
        }
    };

    const handleOpenCompare = async () => {
        if (selectedScenarioIds.size < 2 || loadingCompare) return;

        setLoadingCompare(true);
        try {
            const headers = await getHeaders();
            const scenarioPromises = Array.from(selectedScenarioIds).map(id =>
                fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios/${id}`, { headers })
                    .then(res => {
                        if (!res.ok) throw new Error(`Failed to load scenario ${id}`);
                        return res.json();
                    })
            );

            const fullScenarios = await Promise.all(scenarioPromises);
            setCompareScenarios(fullScenarios);
            setCompareModalOpen(true);
        } catch (error) {
            console.error('Failed to load scenarios for comparison:', error);
            setSnackbar({ open: true, message: 'Failed to load scenario data', severity: 'error' });
            setCompareScenarios([]);
        } finally {
            setLoadingCompare(false);
        }
    };

    const canCompare = selectedScenarioIds.size >= 2;
    const selectableCount = scenarios.filter(s => ['completed', 'optimal', 'Optimal Obtained'].includes(s.status) || s.reopt_results || s.last_run_uuid).length;
    const allSelected = selectableCount > 0 && selectedScenarioIds.size === Math.min(selectableCount, 5);

    const handleDuplicate = async () => {
        if (!selectedScenarioId) return;
        try {
            const headers = await getHeaders();
            const res = await fetch(`${API_BASE_URL}/api/site_dna/v1/projects/${projectId}/scenarios/${selectedScenarioId}/duplicate`, {
                method: "POST",
                headers,
                body: JSON.stringify({ name: duplicateName })
            });
            if (res.ok) {
                setDuplicateDialogOpen(false);
                setSnackbar({ open: true, message: "Scenario duplicated", severity: "success" });
                loadProjectAndScenarios();
            } else {
                const errorData = await res.json();
                throw new Error(errorData.error || "Duplicate failed");
            }
        } catch (e) {
            setSnackbar({ open: true, message: `Duplicate failed: ${e.message}`, severity: "error" });
        }
    };

    if (!project) return (
        <Typography sx={{
            color: isRetro ? '#5fd4c4' : textPrimary,
            p: 4,
            fontFamily: isRetro ? vortxTypography.mono : vortxTypography.body,
            fontSize: isRetro ? '0.85rem' : undefined,
        }}>
            {isRetro ? '> loading project data...' : 'Loading...'}
        </Typography>
    );

    return (
        <Box sx={{
            minHeight: isRetro ? 'auto' : '100vh',
            background: bgMain,
            py: isRetro ? 0 : 6
        }}>
            <Container maxWidth="xl" sx={{ px: isRetro ? 0 : undefined }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={isRetro ? 3 : 4}>
                    <div>
                        {!isRetro && (
                            <Button
                                startIcon={<ArrowBack />}
                                onClick={() => navigate(`/all-projects/${projectId}`)}
                                sx={{
                                    color: textSecondary,
                                    fontFamily: vortxTypography.body,
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    mb: 2,
                                    '&:hover': {
                                        bgcolor: alpha(vortxColors.gold, 0.1),
                                        color: vortxColors.gold
                                    }
                                }}
                            >
                                Back to Project
                            </Button>
                        )}
                        {isRetro ? (
                            <>
                                {/* Retro Header */}
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    mb: 1,
                                }}>
                                    <Box sx={{
                                        width: 4,
                                        height: 32,
                                        background: vortxColors.gold,
                                        boxShadow: `0 0 10px ${vortxColors.gold}`,
                                    }} />
                                    <Typography sx={{
                                        fontFamily: vortxTypography.heading,
                                        fontSize: '1.5rem',
                                        fontWeight: 700,
                                        color: vortxColors.gold,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.1em',
                                        textShadow: `0 0 20px rgba(178, 140, 80, 0.4)`,
                                    }}>
                                        {project?.name || 'Project'} <span style={{ color: '#5fd4c4' }}>//</span> Scenario Matrix
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={openProjectRename}
                                        title="Rename Project"
                                        sx={{
                                            color: retroColors.muted,
                                            borderRadius: 0,
                                            ml: 1,
                                            '&:hover': {
                                                color: vortxColors.gold,
                                                bgcolor: 'rgba(178, 140, 80, 0.1)'
                                            }
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Typography sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.75rem',
                                    color: '#888',
                                    pl: 3,
                                    fontStyle: 'italic',
                                }}>
                                    select a scenario to view or modify
                                </Typography>
                            </>
                        ) : (
                            <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="h3" sx={{ fontFamily: vortxTypography.heading, color: vortxColors.gold, fontWeight: 700, mb: 1, letterSpacing: '0.02em' }}>
                                        {project.name.toUpperCase()}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={openProjectRename}
                                        title="Rename Project"
                                        sx={{
                                            color: vortxColors.gray,
                                            mb: 1,
                                            '&:hover': {
                                                color: vortxColors.gold,
                                                bgcolor: alpha(vortxColors.gold, 0.1)
                                            }
                                        }}
                                    >
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                                <Typography sx={{ fontFamily: vortxTypography.body, color: textSecondary, fontSize: '1.1rem' }}>
                                    Scenario Analysis
                                </Typography>
                            </>
                        )}
                    </div>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                            variant="outlined"
                            startIcon={<ShareIcon />}
                            onClick={() => setSharedPagesOpen(true)}
                            sx={{
                                color: vortxColors.stone,
                                borderColor: alpha(vortxColors.stone, 0.3),
                                px: 2,
                                py: 1,
                                fontWeight: 600,
                                fontFamily: vortxTypography.body,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontSize: '0.8rem',
                                '&:hover': {
                                    borderColor: vortxColors.gold,
                                    color: vortxColors.gold,
                                    bgcolor: alpha(vortxColors.gold, 0.05),
                                },
                            }}
                        >
                            Shared Links
                        </Button>
                        {selectedScenarioIds.size > 0 && (
                            <Button
                                variant={canCompare ? "contained" : "outlined"}
                                startIcon={loadingCompare ? <CircularProgress size={20} color="inherit" /> : <CompareIcon />}
                                onClick={handleOpenCompare}
                                disabled={!canCompare || loadingCompare}
                                sx={{
                                    bgcolor: canCompare ? vortxColors.teal : 'transparent',
                                    borderColor: canCompare ? vortxColors.teal : vortxColors.gray,
                                    color: canCompare ? '#1a1a1a' : vortxColors.gray,
                                    px: 3,
                                    py: 1.5,
                                    fontWeight: 700,
                                    fontFamily: vortxTypography.body,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    '&:hover': {
                                        bgcolor: canCompare ? alpha(vortxColors.teal, 0.85) : alpha(vortxColors.teal, 0.1),
                                        borderColor: vortxColors.teal,
                                    }
                                }}
                            >
                                Compare ({selectedScenarioIds.size})
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            startIcon={isRetro ? <TerminalIcon /> : undefined}
                            onClick={() => navigate(`/projects/${projectId}/scenarios/new`)}
                            sx={isRetro ? {
                                bgcolor: 'transparent',
                                color: '#5fd4c4',
                                px: 3,
                                py: 1,
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                fontFamily: vortxTypography.mono,
                                borderRadius: 0,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                border: '1px solid rgba(95, 212, 196, 0.5)',
                                boxShadow: 'none',
                                '&:hover': {
                                    bgcolor: 'rgba(95, 212, 196, 0.1)',
                                    borderColor: '#5fd4c4',
                                    boxShadow: '0 0 15px rgba(95, 212, 196, 0.3)',
                                }
                            } : {
                                bgcolor: vortxColors.gold,
                                color: '#FFFFFF',
                                px: 4,
                                py: 1.5,
                                fontWeight: 600,
                                fontFamily: vortxTypography.body,
                                borderRadius: 2,
                                textTransform: 'none',
                                boxShadow: `0 2px 8px ${alpha(vortxColors.gold, 0.3)}`,
                                '&:hover': {
                                    bgcolor: '#9e7940',
                                    boxShadow: `0 4px 12px ${alpha(vortxColors.gold, 0.4)}`
                                }
                            }}
                        >
                            New Scenario
                        </Button>
                    </Box>
                </Box>

                <TableContainer
                    component={Paper}
                    sx={isRetro ? {
                        bgcolor: 'rgba(0, 0, 0, 0.4)',
                        borderRadius: 0,
                        border: '1px solid rgba(63, 106, 100, 0.3)',
                        boxShadow: 'none',
                    } : {
                        bgcolor: bgCard,
                        borderRadius: 3,
                        border: `1px solid ${alpha(vortxColors.teal, 0.2)}`,
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)'
                    }}
                >
                    <Table>
                        <TableHead>
                            <TableRow sx={isRetro ? {
                                bgcolor: 'rgba(63, 106, 100, 0.15)',
                            } : {
                                bgcolor: alpha(vortxColors.warmStone, 0.5)
                            }}>
                                <TableCell padding="checkbox" sx={isRetro ? {
                                    borderBottom: '1px solid rgba(178, 140, 80, 0.4)',
                                } : {
                                    borderBottom: `2px solid ${vortxColors.gold}`
                                }}>
                                    <Tooltip title={selectableCount === 0 ? "No scenarios with results" : "Select all"}>
                                        <span>
                                            <Checkbox
                                                checked={allSelected}
                                                indeterminate={selectedScenarioIds.size > 0 && !allSelected}
                                                onChange={toggleSelectAll}
                                                disabled={selectableCount === 0}
                                                sx={{ color: vortxColors.gray, '&.Mui-checked': { color: vortxColors.gold } }}
                                            />
                                        </span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell sx={isRetro ? {
                                    color: vortxColors.gold,
                                    fontWeight: 600,
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                    borderBottom: '1px solid rgba(178, 140, 80, 0.4)',
                                } : {
                                    color: textPrimary, fontWeight: 700, fontFamily: vortxTypography.body, borderBottom: `2px solid ${vortxColors.gold}`
                                }}>Scenario</TableCell>
                                <TableCell sx={isRetro ? {
                                    color: vortxColors.gold,
                                    fontWeight: 600,
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                    borderBottom: '1px solid rgba(178, 140, 80, 0.4)',
                                } : {
                                    color: textPrimary, fontWeight: 700, fontFamily: vortxTypography.body, borderBottom: `2px solid ${vortxColors.gold}`
                                }}>Status</TableCell>
                                <TableCell sx={isRetro ? {
                                    color: vortxColors.gold,
                                    fontWeight: 600,
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                    borderBottom: '1px solid rgba(178, 140, 80, 0.4)',
                                } : {
                                    color: textPrimary, fontWeight: 700, fontFamily: vortxTypography.body, borderBottom: `2px solid ${vortxColors.gold}`
                                }}>System Config</TableCell>
                                <TableCell sx={isRetro ? {
                                    color: vortxColors.gold,
                                    fontWeight: 600,
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                    borderBottom: '1px solid rgba(178, 140, 80, 0.4)',
                                } : {
                                    color: textPrimary, fontWeight: 700, fontFamily: vortxTypography.body, borderBottom: `2px solid ${vortxColors.gold}`
                                }}>Created</TableCell>
                                <TableCell align="center" sx={isRetro ? {
                                    color: vortxColors.gold,
                                    fontWeight: 600,
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.75rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.1em',
                                    borderBottom: '1px solid rgba(178, 140, 80, 0.4)',
                                } : {
                                    color: textPrimary, fontWeight: 700, fontFamily: vortxTypography.body, borderBottom: `2px solid ${vortxColors.gold}`
                                }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {scenarios.map((scenario) => {
                                // Check status, reopt_results, or last_run_uuid as indicators that scenario has results
                                // last_run_uuid is a fallback for older scenarios that don't have status set on scenario doc
                                const hasResults = ['completed', 'optimal', 'Optimal Obtained'].includes(scenario.status) || !!scenario.reopt_results || !!scenario.last_run_uuid;
                                const isSelected = selectedScenarioIds.has(scenario.id);

                                return (
                                <TableRow
                                    key={scenario.id}
                                    sx={isRetro ? {
                                        bgcolor: isSelected ? 'rgba(178, 140, 80, 0.15)' : undefined,
                                        '&:hover': {
                                            bgcolor: 'rgba(178, 140, 80, 0.1)',
                                            '& td': { borderColor: 'rgba(178, 140, 80, 0.3)' }
                                        },
                                        '& td': {
                                            borderBottom: '1px solid rgba(63, 106, 100, 0.2)',
                                        }
                                    } : {
                                        bgcolor: isSelected ? alpha(vortxColors.gold, 0.1) : undefined,
                                        '&:hover': {
                                            bgcolor: `rgba(178, 140, 80, 0.15)`
                                        },
                                        '&:nth-of-type(even)': {
                                            bgcolor: isSelected ? alpha(vortxColors.gold, 0.1) : 'rgba(166, 161, 155, 0.1)'
                                        }
                                    }}
                                >
                                    <TableCell padding="checkbox">
                                        <Tooltip title={hasResults ? "" : "Run optimization first"}>
                                            <span>
                                                <Checkbox
                                                    checked={isSelected}
                                                    onChange={() => toggleScenarioSelection(scenario.id)}
                                                    disabled={!hasResults}
                                                    sx={{ color: vortxColors.gray, '&.Mui-checked': { color: vortxColors.gold } }}
                                                />
                                            </span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell component="th" scope="row">
                                        <Typography
                                            sx={isRetro ? {
                                                cursor: 'pointer',
                                                color: '#5fd4c4',
                                                textDecoration: 'none',
                                                fontWeight: 500,
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.8rem',
                                                '&:hover': {
                                                    color: vortxColors.gold,
                                                    textShadow: `0 0 10px rgba(178, 140, 80, 0.5)`,
                                                }
                                            } : {
                                                cursor: 'pointer',
                                                color: vortxColors.gold,
                                                textDecoration: 'none',
                                                '&:hover': { textDecoration: 'underline' },
                                                fontWeight: 500,
                                                fontFamily: vortxTypography.body
                                            }}
                                            onClick={() => navigate(`/projects/${projectId}/scenarios/${scenario.id}`)}
                                        >
                                            {scenario.name}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {isRetro ? (
                                            <Typography sx={{
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.7rem',
                                                color: (['completed', 'optimal', 'Optimal Obtained'].includes(scenario.status) || scenario.reopt_results || scenario.last_run_uuid) ? '#5fd4c4' : retroColors.muted,
                                                textTransform: 'uppercase',
                                            }}>
                                                {scenario.status || (scenario.reopt_results || scenario.last_run_uuid ? 'complete' : 'draft')}
                                            </Typography>
                                        ) : (
                                            <Chip
                                                label={scenario.status || (scenario.reopt_results || scenario.last_run_uuid ? 'Completed' : 'Draft')}
                                                size="small"
                                                sx={{
                                                    bgcolor: (['completed', 'optimal', 'Optimal Obtained'].includes(scenario.status) || scenario.reopt_results || scenario.last_run_uuid) ? alpha(vortxColors.teal, 0.2) : alpha(vortxColors.gray, 0.2),
                                                    color: (['completed', 'optimal', 'Optimal Obtained'].includes(scenario.status) || scenario.reopt_results || scenario.last_run_uuid) ? vortxColors.teal : textSecondary,
                                                    fontWeight: 600
                                                }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell sx={isRetro ? {
                                        color: '#888',
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.7rem',
                                    } : {
                                        color: textPrimary,
                                        fontFamily: vortxTypography.body
                                    }}>
                                        {(() => {
                                            // Use system_config_summary from the API (saved in Firestore metadata)
                                            const config = scenario.system_config_summary || {};
                                            const parts = [];

                                            // PV info - add (E) suffix for existing solar
                                            if (config.pv_kw && config.pv_kw > 0) {
                                                const pvLabel = config.pv_existing ? 'PV (E)' : 'PV';
                                                parts.push(`${pvLabel}: ${Number(config.pv_kw).toFixed(0)} kW`);
                                            }

                                            // Storage info
                                            if (config.storage_kw > 0 || config.storage_kwh > 0) {
                                                parts.push(`Storage: ${Number(config.storage_kw || 0).toFixed(0)} kW / ${Number(config.storage_kwh || 0).toFixed(0)} kWh`);
                                            }

                                            // Generator info
                                            if (config.generator_kw && config.generator_kw > 0) {
                                                parts.push(`Generator: ${Number(config.generator_kw).toFixed(0)} kW`);
                                            }

                                            return parts.length > 0 ? parts.join(' • ') : (scenario.description || (isRetro ? '—' : 'No system configured'));
                                        })()}
                                    </TableCell>
                                    <TableCell sx={isRetro ? {
                                        color: retroColors.muted,
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.7rem',
                                    } : {
                                        color: textPrimary,
                                        fontFamily: vortxTypography.body
                                    }}>
                                        {scenario.created_at ? new Date(scenario.created_at).toLocaleDateString() : '-'}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Button
                                            size="small"
                                            startIcon={<ViewIcon />}
                                            onClick={() => navigate(`/projects/${projectId}/scenarios/${scenario.id}`)}
                                            sx={isRetro ? {
                                                mr: 1,
                                                color: '#5fd4c4',
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.7rem',
                                                fontWeight: 600,
                                                textTransform: 'uppercase',
                                                borderRadius: 0,
                                                '&:hover': {
                                                    bgcolor: 'rgba(95, 212, 196, 0.1)',
                                                    color: '#5fd4c4',
                                                }
                                            } : {
                                                mr: 1,
                                                color: vortxColors.teal,
                                                fontFamily: vortxTypography.body,
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                '&:hover': {
                                                    bgcolor: alpha(vortxColors.gold, 0.1),
                                                    color: vortxColors.gold
                                                }
                                            }}
                                        >
                                            {isRetro ? 'Open' : 'View'}
                                        </Button>
                                        {(['completed', 'optimal', 'Optimal Obtained'].includes(scenario.status) || scenario.reopt_results || scenario.last_run_uuid) && (
                                        <IconButton
                                            size="small"
                                            onClick={() => openShareDialog(scenario.id, scenario.name)}
                                            title="Share Scenario"
                                            sx={isRetro ? {
                                                color: retroColors.muted,
                                                borderRadius: 0,
                                                '&:hover': { color: vortxColors.gold }
                                            } : {
                                                color: vortxColors.gray,
                                                '&:hover': { color: vortxColors.gold }
                                            }}
                                        >
                                            <ShareIcon fontSize="small" />
                                        </IconButton>
                                        )}
                                        <IconButton
                                            size="small"
                                            onClick={() => openDuplicate(scenario.id, scenario.name)}
                                            title="Duplicate Scenario"
                                            sx={isRetro ? {
                                                color: retroColors.muted,
                                                borderRadius: 0,
                                                '&:hover': { color: '#5fd4c4' }
                                            } : {
                                                color: vortxColors.gray,
                                                '&:hover': { color: vortxColors.teal }
                                            }}
                                        >
                                            <DuplicateIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => openRename(scenario.id, scenario.name)}
                                            title="Rename Scenario"
                                            sx={isRetro ? {
                                                color: retroColors.muted,
                                                borderRadius: 0,
                                                '&:hover': { color: vortxColors.gold }
                                            } : {
                                                color: vortxColors.gray,
                                                '&:hover': { color: vortxColors.gold }
                                            }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => openDelete(scenario.id, scenario.name)}
                                            title="Delete Scenario"
                                            sx={isRetro ? {
                                                color: retroColors.muted,
                                                borderRadius: 0,
                                                '&:hover': { color: '#e74c3c' }
                                            } : {
                                                color: vortxColors.gray,
                                                '&:hover': { color: '#ff6b6b' }
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                                );
                            })}
                            {scenarios.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={isRetro ? {
                                        color: retroColors.muted,
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.8rem',
                                        py: 4,
                                        fontStyle: 'italic',
                                    } : {
                                        color: vortxColors.charcoal,
                                        fontFamily: vortxTypography.body,
                                        py: 4
                                    }}>
                                        {isRetro ? '> no scenarios initialized. create a scenario to begin analysis.' : 'No scenarios yet. Create a scenario to start.'}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Rename Dialog */}
                <Dialog
                    open={renameDialogOpen}
                    onClose={() => setRenameDialogOpen(false)}
                    slotProps={{
                        paper: {
                            sx: {
                                bgcolor: vortxColors.charcoal,
                                borderRadius: 3,
                                border: `1px solid ${alpha(vortxColors.gold, 0.3)}`
                            }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontFamily: vortxTypography.heading, color: vortxColors.gold }}>Rename Scenario</DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Scenario Name"
                            fullWidth
                            value={tempName}
                            onChange={(e) => setTempName(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#FFFFFF',
                                    fontFamily: vortxTypography.body,
                                    bgcolor: 'transparent',
                                    '& fieldset': { borderColor: alpha(vortxColors.gold, 0.5) },
                                    '&:hover fieldset': { borderColor: vortxColors.gold },
                                    '&.Mui-focused fieldset': { borderColor: vortxColors.gold }
                                },
                                '& .MuiInputLabel-root': { color: vortxColors.gold, fontFamily: vortxTypography.body },
                                '& .MuiInputLabel-root.Mui-focused': { color: vortxColors.gold }
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRenameDialogOpen(false)} sx={{ color: vortxColors.gray }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleRename}
                            variant="contained"
                            sx={{
                                bgcolor: vortxColors.gold,
                                color: '#FFFFFF',
                                fontFamily: vortxTypography.body,
                                fontWeight: 600,
                                '&:hover': { bgcolor: alpha(vortxColors.gold, 0.9) }
                            }}
                        >
                            Rename
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                    slotProps={{
                        paper: {
                            sx: {
                                bgcolor: vortxColors.charcoal,
                                borderRadius: 3,
                                border: `1px solid ${alpha(vortxColors.gold, 0.3)}`
                            }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontFamily: vortxTypography.heading, color: vortxColors.gold }}>Delete Scenario?</DialogTitle>
                    <DialogContent>
                        <Typography sx={{ fontFamily: vortxTypography.body, color: '#FFFFFF' }}>
                            Are you sure you want to delete <strong style={{ color: vortxColors.gold }}>{selectedScenarioName}</strong>?
                            This will remove all associated runs and results.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: vortxColors.gray }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDelete}
                            variant="contained"
                            sx={{
                                bgcolor: '#ff6b6b',
                                '&:hover': { bgcolor: '#ff5252' }
                            }}
                        >
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Duplicate Dialog */}
                <Dialog
                    open={duplicateDialogOpen}
                    onClose={() => setDuplicateDialogOpen(false)}
                    slotProps={{
                        paper: {
                            sx: {
                                bgcolor: vortxColors.charcoal,
                                borderRadius: 3,
                                border: `1px solid ${alpha(vortxColors.gold, 0.3)}`
                            }
                        }
                    }}
                >
                    <DialogTitle sx={{ fontFamily: vortxTypography.heading, color: vortxColors.gold }}>Duplicate Scenario</DialogTitle>
                    <DialogContent>
                        <Typography sx={{ fontFamily: vortxTypography.body, color: '#FFFFFF', mb: 2 }}>
                            Create a copy of <strong style={{ color: vortxColors.gold }}>{selectedScenarioName}</strong>
                        </Typography>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="New Scenario Name"
                            fullWidth
                            value={duplicateName}
                            onChange={(e) => setDuplicateName(e.target.value)}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    color: '#FFFFFF',
                                    fontFamily: vortxTypography.body,
                                    bgcolor: 'transparent',
                                    '& fieldset': { borderColor: alpha(vortxColors.gold, 0.5) },
                                    '&:hover fieldset': { borderColor: vortxColors.gold },
                                    '&.Mui-focused fieldset': { borderColor: vortxColors.gold }
                                },
                                '& .MuiInputLabel-root': { color: vortxColors.gold, fontFamily: vortxTypography.body },
                                '& .MuiInputLabel-root.Mui-focused': { color: vortxColors.gold }
                            }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDuplicateDialogOpen(false)} sx={{ color: vortxColors.gray }}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDuplicate}
                            variant="contained"
                            sx={{
                                bgcolor: vortxColors.gold,
                                color: '#FFFFFF',
                                fontFamily: vortxTypography.body,
                                fontWeight: 600,
                                '&:hover': { bgcolor: alpha(vortxColors.gold, 0.9) }
                            }}
                        >
                            Duplicate
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Project Rename Dialog */}
                <Dialog
                    open={projectRenameDialogOpen}
                    onClose={() => setProjectRenameDialogOpen(false)}
                    slotProps={{
                        paper: {
                            sx: isRetro ? {
                                bgcolor: 'rgba(10, 18, 16, 0.98)',
                                borderRadius: 0,
                                border: '1px solid rgba(178, 140, 80, 0.5)',
                                boxShadow: '0 0 30px rgba(178, 140, 80, 0.2)',
                                width: '100%',
                                maxWidth: 450
                            } : {
                                bgcolor: vortxColors.charcoal,
                                borderRadius: 3,
                                border: `1px solid ${alpha(vortxColors.gold, 0.3)}`
                            }
                        }
                    }}
                >
                    <DialogTitle sx={isRetro ? {
                        fontFamily: vortxTypography.heading,
                        color: vortxColors.gold,
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                        pb: 1,
                        borderBottom: '1px solid rgba(63, 106, 100, 0.3)',
                    } : {
                        fontFamily: vortxTypography.heading,
                        color: vortxColors.gold
                    }}>
                        Rename Project
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            label="Project Name"
                            fullWidth
                            value={tempProjectName}
                            onChange={(e) => setTempProjectName(e.target.value)}
                            sx={isRetro ? {
                                mt: 2,
                                '& .MuiOutlinedInput-root': {
                                    color: '#f4f1ec',
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.9rem',
                                    bgcolor: 'rgba(0, 0, 0, 0.3)',
                                    borderRadius: 0,
                                    '& fieldset': {
                                        borderColor: 'rgba(63, 106, 100, 0.5)',
                                        borderRadius: 0,
                                    },
                                    '&:hover fieldset': {
                                        borderColor: vortxColors.gold
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: vortxColors.gold,
                                        boxShadow: `0 0 10px rgba(178, 140, 80, 0.2)`,
                                    }
                                },
                                '& .MuiInputLabel-root': {
                                    color: '#888',
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.8rem',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: vortxColors.gold
                                }
                            } : {
                                '& .MuiOutlinedInput-root': {
                                    color: '#FFFFFF',
                                    fontFamily: vortxTypography.body,
                                    bgcolor: 'transparent',
                                    '& fieldset': { borderColor: alpha(vortxColors.gold, 0.5) },
                                    '&:hover fieldset': { borderColor: vortxColors.gold },
                                    '&.Mui-focused fieldset': { borderColor: vortxColors.gold }
                                },
                                '& .MuiInputLabel-root': { color: vortxColors.gold, fontFamily: vortxTypography.body },
                                '& .MuiInputLabel-root.Mui-focused': { color: vortxColors.gold }
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={isRetro ? { p: 2 } : undefined}>
                        <Button
                            onClick={() => setProjectRenameDialogOpen(false)}
                            sx={isRetro ? {
                                color: '#888',
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                borderRadius: 0,
                                '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.05)'
                                }
                            } : {
                                color: vortxColors.gray
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleProjectRename}
                            variant="contained"
                            sx={isRetro ? {
                                bgcolor: 'transparent',
                                color: vortxColors.gold,
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontWeight: 600,
                                px: 3,
                                borderRadius: 0,
                                border: `1px solid ${vortxColors.gold}`,
                                boxShadow: 'none',
                                '&:hover': {
                                    bgcolor: 'rgba(178, 140, 80, 0.1)',
                                    boxShadow: `0 0 15px rgba(178, 140, 80, 0.3)`,
                                }
                            } : {
                                bgcolor: vortxColors.gold,
                                color: '#FFFFFF',
                                fontFamily: vortxTypography.body,
                                fontWeight: 600,
                                '&:hover': { bgcolor: alpha(vortxColors.gold, 0.9) }
                            }}
                        >
                            Rename
                        </Button>
                    </DialogActions>
                </Dialog>

                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                >
                    <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
                </Snackbar>

                {compareViewMode === 'dashboard' ? (
                    <ScenarioCompareModalV2
                        open={compareModalOpen}
                        onClose={() => setCompareModalOpen(false)}
                        scenarios={compareScenarios}
                        projectId={projectId}
                        projectName={project?.name}
                        onToggleView={handleToggleCompareView}
                    />
                ) : (
                    <ScenarioCompareModal
                        open={compareModalOpen}
                        onClose={() => setCompareModalOpen(false)}
                        scenarios={compareScenarios}
                        projectName={project?.name}
                        onToggleView={handleToggleCompareView}
                    />
                )}

                <SharedPagesDashboard
                    open={sharedPagesOpen}
                    onClose={() => setSharedPagesOpen(false)}
                />

                {/* Single-Scenario Share Dialog */}
                {shareScenarioId && (
                    <ShareConfigDialog
                        open={shareDialogOpen}
                        onClose={() => {
                            setShareDialogOpen(false);
                            setShareScenarioId(null);
                            setShareScenarioData(null);
                        }}
                        projectId={projectId}
                        projectName={project?.name || 'Project'}
                        scenarioIds={[shareScenarioId]}
                        scenarioNames={[shareScenarioName]}
                        comparisonData={shareScenarioData ? { scenarios: [shareScenarioData], projectName: project?.name } : null}
                    />
                )}
            </Container>
        </Box>
    );
}
